import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, BookOpen, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Layout from '../components/layout/Layout';
import { departments, programmes, images } from '../data/mock';

const Departments = () => {
  const getProgrammesByDepartment = (deptId) => {
    return programmes.filter((p) => p.departmentId === deptId);
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${images.techLab})` }}
        >
          <div className="absolute inset-0 overlay-navy" />
        </div>
        <div className="container-custom relative z-10">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
            Our Departments
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Explore our three specialized departments offering comprehensive programmes for diverse career paths.
          </p>
        </div>
      </section>

      {/* Departments List */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="space-y-16">
            {departments.map((dept, index) => {
              const deptProgrammes = getProgrammesByDepartment(dept.id);
              return (
                <div
                  key={dept.id}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="relative">
                      <img
                        src={dept.image}
                        alt={dept.name}
                        className="w-full h-[350px] object-cover rounded-2xl shadow-xl"
                      />
                      <div className="absolute top-4 right-4">
                        <div className="flex gap-2">
                          {dept.intakes.map((intake) => (
                            <Badge
                              key={intake}
                              className="bg-[#d4a855] text-[#142942] font-medium"
                            >
                              {intake} Intake
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <Badge className="bg-[#1e3a5f]/10 text-[#1e3a5f] mb-4">
                      {deptProgrammes.length} Programme{deptProgrammes.length !== 1 ? 's' : ''}
                    </Badge>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-[#1e3a5f] mb-4">
                      {dept.name}
                    </h2>
                    <p className="text-[#475569] leading-relaxed mb-6">
                      {dept.description}
                    </p>
                    
                    <div className="mb-6">
                      <h4 className="font-semibold text-[#1e3a5f] mb-3 flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-[#d4a855]" />
                        Programmes Offered
                      </h4>
                      <ul className="space-y-2">
                        {deptProgrammes.map((prog) => (
                          <li key={prog.id}>
                            <Link
                              to={`/programmes/${prog.id}`}
                              className="text-[#475569] hover:text-[#2563eb] transition-colors flex items-center gap-2 group"
                            >
                              <ChevronRight className="w-4 h-4 text-[#d4a855]" />
                              <span>{prog.name}</span>
                              <span className="text-xs text-[#94a3b8]">(NTA {prog.ntaLevels.join('-')})</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {dept.id === 'health-allied-sciences' && (
                      <div className="bg-[#fef3c7] border border-[#d4a855] rounded-lg p-4 mb-6">
                        <p className="text-sm text-[#92400e] flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <strong>Note:</strong> Health & Allied Sciences programmes are available only during the September intake.
                        </p>
                      </div>
                    )}

                    <Link to={`/departments/${dept.id}`}>
                      <Button className="bg-[#1e3a5f] hover:bg-[#2563eb] text-white">
                        Learn More
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1e3a5f]">
        <div className="container-custom text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            Find Your Path
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-8">
            Not sure which department is right for you? Explore all our programmes or get in touch with our admissions team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/programmes">
              <Button size="lg" className="bg-[#d4a855] hover:bg-[#f4d794] text-[#142942] font-semibold px-8">
                Browse All Programmes
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#1e3a5f] px-8">
                Contact Admissions
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Departments;
